import { Mark, mergeAttributes } from "@tiptap/react";
import {
  coerceHighlightColor,
  DEFAULT_HIGHLIGHT_COLOR,
  HIGHLIGHT_COLORS,
  type HighlightColor,
} from "./highlight-colors";

const HIGHLIGHT_COLOR_ATTR = "color";
const HIGHLIGHT_COLOR_DATA_ATTR = "data-highlight-color";

// Re-exported so editor code keeps one entry point for the mark and its color vocabulary.
export { DEFAULT_HIGHLIGHT_COLOR, HIGHLIGHT_COLORS, type HighlightColor };

interface HighlightAttributes {
  color: HighlightColor;
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    highlight: {
      setHighlight: (attributes?: Partial<HighlightAttributes>) => ReturnType;
      toggleHighlight: (attributes?: Partial<HighlightAttributes>) => ReturnType;
      unsetHighlight: () => ReturnType;
    };
  }
}

/**
 * Inline highlight mark used to flag placeholder or unfinished content.
 *
 * Colors are rendered as a data attribute rather than inline styles so light
 * and dark theming stays in CSS. A bare `<mark>` (external paste, markdown)
 * parses as the default yellow.
 */
export const HighlightMark = Mark.create({
  name: "highlight",

  addAttributes() {
    return {
      [HIGHLIGHT_COLOR_ATTR]: {
        default: DEFAULT_HIGHLIGHT_COLOR,
        parseHTML: (element: HTMLElement) =>
          coerceHighlightColor(element.getAttribute(HIGHLIGHT_COLOR_DATA_ATTR)),
        renderHTML: (attributes: Record<string, unknown>) => ({
          [HIGHLIGHT_COLOR_DATA_ATTR]: coerceHighlightColor(attributes[HIGHLIGHT_COLOR_ATTR]),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `mark[${HIGHLIGHT_COLOR_DATA_ATTR}]` }, { tag: "mark" }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ["mark", mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setHighlight:
        (attributes) =>
        ({ commands }) =>
          commands.setMark(this.name, {
            [HIGHLIGHT_COLOR_ATTR]: coerceHighlightColor(attributes?.color),
          }),
      toggleHighlight:
        (attributes) =>
        ({ commands, editor }) => {
          const color = coerceHighlightColor(attributes?.color);
          if (editor.isActive(this.name, { [HIGHLIGHT_COLOR_ATTR]: color })) {
            return commands.unsetMark(this.name);
          }
          return commands.setMark(this.name, { [HIGHLIGHT_COLOR_ATTR]: color });
        },
      unsetHighlight:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});
